import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SubscriptionLimitBanner } from '@/components/shared/SubscriptionLimitBanner';
import { MethodologyContextPicker } from '@/components/methodologies/MethodologyContextPicker';
import type { CatalogMethod, ContextOverrides, MethodInputField } from '@/types/methodologies';
import type { Project, Persona } from '@/utils/pocketbase';

const NO_PERSONA_VALUE = '__none__';

// Fixed two-valued fields the backend sends as a plain `text` input (same
// trick as `personaId` below) — the frontend renders them as a Select
// because the value set is closed, not because the wire type says so.
const SOURCE_TYPE_VALUES = ['interview', 'survey'] as const;

// A textarea this long (research-analysis's `material`, up to 60 000 chars)
// gets a file-upload affordance and the privacy reminder alongside it; a
// short one (focus, hypotheses, goal) does not. Picked well above every
// wave-A field's maxLength (1000) and below `material`'s (60 000).
const FILE_UPLOAD_MAX_LENGTH_THRESHOLD = 10_000;
const UPLOAD_ACCEPT = '.txt,.csv,.md,text/plain,text/csv,text/markdown';

interface MethodologyGenerateFormProps {
  catalogMethod: CatalogMethod;
  isFree: boolean;
  projects: Project[];
  personas: Persona[];
  selectedProjectId: string;
  onSelectProject: (projectId: string) => void;
  selectedPersonaId: string;
  onSelectPersona: (personaId: string) => void;
  inputValues: Record<string, string>;
  onInputChange: (key: string, value: string) => void;
  onContextChange: (overrides: ContextOverrides) => void;
  onGenerate: () => void;
  isGenerating: boolean;
}

function UploadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 15V3M12 3L7 8M12 3L17 8" stroke="#565858" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4 15V17C4 19.2091 5.79086 21 8 21H16C18.2091 21 20 19.2091 20 17V15" stroke="#565858" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** One generic field: a text `Input` or a `Textarea`, with an optional file-upload affordance for long textareas. */
function GenericField({
  field,
  value,
  onChange,
  disabled,
}: {
  field: MethodInputField;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation('methodologies');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const allowsFileUpload = field.type === 'textarea' && field.maxLength >= FILE_UPLOAD_MAX_LENGTH_THRESHOLD;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      onChange(text.slice(0, field.maxLength));
    };
    reader.readAsText(file);
  };

  return (
    <div className="flex flex-col gap-[8px]">
      <div className="flex items-center justify-between gap-[8px]">
        <Label htmlFor={`method-field-${field.key}`} className="font-segoe text-[14px] text-[#565858]">{t(`fields.${field.key}.label`)}{field.required && <span aria-hidden="true"> *</span>}</Label>
        {allowsFileUpload && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={UPLOAD_ACCEPT}
              onChange={handleFileChange}
              disabled={disabled}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              className="flex items-center gap-[6px] font-segoe font-semibold text-[13px] leading-[18px] text-[#565858] hover:text-[#0d0d0d] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <UploadIcon />
              {t('fields.uploadFile.button')}
            </button>
          </>
        )}
      </div>

      {allowsFileUpload && (
        <p className="font-segoe text-[12px] leading-[16px] text-[#969696]">
          {t('fields.uploadFile.privacyHint')}
        </p>
      )}

      {field.type === 'textarea' ? (
        <Textarea
          id={`method-field-${field.key}`}
          required={field.required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t(`fields.${field.key}.placeholder`, { defaultValue: '' })}
          rows={allowsFileUpload ? 8 : 3}
          maxLength={field.maxLength}
          disabled={disabled}
          className="rounded-xl border-[#eeeeee] text-[16px] resize-none"
        />
      ) : (
        <Input
          id={`method-field-${field.key}`}
          required={field.required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={t(`fields.${field.key}.placeholder`, { defaultValue: '' })}
          maxLength={field.maxLength}
          disabled={disabled}
          className="h-12 rounded-xl border-[#eeeeee] text-[16px]"
        />
      )}
    </div>
  );
}

export function MethodologyGenerateForm({
  catalogMethod,
  isFree,
  projects,
  personas,
  selectedProjectId,
  onSelectProject,
  selectedPersonaId,
  onSelectPersona,
  inputValues,
  onInputChange,
  onContextChange,
  onGenerate,
  isGenerating,
}: MethodologyGenerateFormProps) {
  const { t } = useTranslation('methodologies');

  if (isFree) {
    return (
      <SubscriptionLimitBanner
        title={t('paidBanner.title')}
        description={t('paidBanner.description')}
        buttonText={t('paidBanner.button')}
      />
    );
  }

  if (!catalogMethod.available) {
    return (
      <SubscriptionLimitBanner
        title={t('limitBanner.title')}
        description={t('limitBanner.description')}
        buttonText={t('paidBanner.button')}
      />
    );
  }

  const hasPersonaField = catalogMethod.inputs.some((field) => field.key === 'personaId');
  const hasSourceTypeField = catalogMethod.inputs.some((field) => field.key === 'sourceType');
  const genericFields = catalogMethod.inputs.filter(
    (field) => field.key !== 'personaId' && field.key !== 'sourceType',
  );

  // The persona field already decides which persona goes into the prompt.
  const contextSources = (catalogMethod.contextSources ?? []).filter(
    (source) => !(hasPersonaField && source === 'personas'),
  );

  const projectMissing = catalogMethod.requiresProject && !selectedProjectId;
  const missingRequiredField = genericFields.some(
    (field) => field.required && !(inputValues[field.key] ?? '').trim(),
  );
  const canSubmit = !isGenerating && !projectMissing && !missingRequiredField;

  return (
    <div className="flex flex-col gap-[16px] bg-white rounded-[16px] p-[24px] border border-[#eeeeee]">
      {catalogMethod.id === 'hadi' && (
        <p className="font-segoe text-sm leading-5 text-[#565858]">{t('hadi.formHint')}</p>
      )}
      {catalogMethod.requiresProject && (
        <div className="flex flex-col gap-[8px]">
          <Label className="font-segoe text-[14px] text-[#565858]">{t('fields.project.label')}</Label>
          <Select value={selectedProjectId} onValueChange={onSelectProject} disabled={isGenerating}>
            <SelectTrigger>
              <SelectValue placeholder={t('fields.project.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {projects.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  {project.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {hasPersonaField && (
        <div className="flex flex-col gap-[8px]">
          <Label className="font-segoe text-[14px] text-[#565858]">{t('fields.personaId.label')}</Label>
          <Select
            value={selectedPersonaId || NO_PERSONA_VALUE}
            onValueChange={(value) => onSelectPersona(value === NO_PERSONA_VALUE ? '' : value)}
            disabled={isGenerating || !selectedProjectId}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('fields.personaId.placeholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_PERSONA_VALUE}>{t('fields.personaId.placeholder')}</SelectItem>
              {personas.map((persona) => (
                <SelectItem key={persona.id} value={persona.id}>
                  {persona.persona_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {selectedProjectId && contextSources.length > 0 && (
        <MethodologyContextPicker
          projectId={selectedProjectId}
          sources={contextSources}
          onChange={onContextChange}
          disabled={isGenerating}
        />
      )}

      {hasSourceTypeField && (
        <div className="flex flex-col gap-[8px]">
          <Label className="font-segoe text-[14px] text-[#565858]">{t('fields.sourceType.label')}</Label>
          <Select
            value={inputValues.sourceType || SOURCE_TYPE_VALUES[0]}
            onValueChange={(value) => onInputChange('sourceType', value)}
            disabled={isGenerating}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SOURCE_TYPE_VALUES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`fields.sourceType.options.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {genericFields.map((field) => (
        <GenericField
          key={field.key}
          field={field}
          value={inputValues[field.key] ?? ''}
          onChange={(value) => onInputChange(field.key, value)}
          disabled={isGenerating}
        />
      ))}

      <button
        type="button"
        onClick={onGenerate}
        disabled={!canSubmit}
        className={`h-[48px] rounded-[16px] font-segoe font-semibold text-[16px] leading-[24px] text-white transition-colors ${
          canSubmit ? 'bg-[#2197F1] hover:bg-[#1a7acc]' : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed'
        }`}
      >
        {t('generateButton')}
      </button>
    </div>
  );
}
